// Write flows back to the cloud via POST /v1/sync (round-trips to the iOS app).
// LWW: existing records push with baseVersion = current version; on conflict the
// server copy wins and we adopt it. New records (ideas, makeovers) push baseVersion 0.

import { presignPutAssets, putBytes, syncPush } from "./apiClient";
import type { SyncPushRejection, SyncPushResult } from "./apiClient";
import type { EntityStore } from "./entityStore";
import type { AssetRef, EntityType, SessionPayload, SyncPushRecord, SyncRecord } from "./types";
import { newUuid, sha256Hex } from "./util";

/**
 * Sharing-aware backends reject records the caller may not write (viewer edits,
 * an editor's PROJECT tombstone, ex-members) via the additive `rejected[]` field.
 * Rejected records must NEVER be applied locally — local state would silently
 * diverge from the server. Old backends never reject (field absent).
 */
function rejectionOf(
  res: SyncPushResult,
  type: EntityType,
  id: string,
): SyncPushRejection | undefined {
  return res.rejected?.find((r) => r.entityType === type && r.entityId === id);
}

function rejectionError(reason: string | undefined, what: string): Error {
  switch (reason) {
    case "owner_only":
      return new Error(`Only the project owner can ${what}.`);
    case "forbidden_role":
    case "not_a_member":
      return new Error(`You don't have permission to ${what} in this shared project.`);
    default:
      return new Error(`The server rejected this change${reason ? ` (${reason})` : ""}.`);
  }
}

/**
 * Soft-delete (tombstone) an entity and ALL its synced descendants in one push batch, mirroring
 * the iOS SyncDelete cascade so a web delete round-trips to the app and other devices. Children
 * are tombstoned before parents. This is the web's delete-parity capability AND the tool for
 * pruning records the app orphaned in the cloud via old hard-deletes (e.g. deleted scans). The
 * local store is updated immediately; conflicts (record edited elsewhere) re-tombstone at the
 * server's version, since a delete is terminal.
 */
export async function deleteEntityCascade(
  store: EntityStore,
  type: EntityType,
  id: string,
): Promise<void> {
  const targets: { type: EntityType; id: string; parentId: string | null; version: number }[] = [];
  const add = (t: EntityType, e: { id: string; parentId?: string | null; version: number }) =>
    targets.push({ type: t, id: e.id, parentId: e.parentId ?? null, version: e.version });

  const collectRoom = (roomId: string, parentId: string | null, version: number) => {
    for (const s of store.sessions(roomId)) {
      for (const c of store.cycles(s.id)) add("CYCLE", c);
      add("SESSION", s);
    }
    for (const p of store.photos(roomId)) add("PHOTO", p);
    for (const sc of store.scans(roomId)) add("SCAN", sc);
    for (const i of store.ideas(roomId)) add("IDEA", i);
    add("ROOM", { id: roomId, parentId, version });
  };

  if (type === "PROJECT") {
    for (const r of store.rooms(id)) collectRoom(r.id, r.parentId, r.version);
    const proj = store.get("PROJECT", id);
    if (proj) add("PROJECT", proj);
  } else if (type === "ROOM") {
    const room = store.get("ROOM", id);
    if (room) collectRoom(id, room.parentId, room.version);
  } else {
    const e = store.get(type, id);
    if (e) add(type, e);
  }
  if (!targets.length) return;

  const parentOf = new Map(targets.map((t) => [t.type + "#" + t.id, t.parentId]));
  let records: SyncPushRecord[] = targets.map((t) => ({
    entityType: t.type,
    entityId: t.id,
    parentId: t.parentId,
    baseVersion: t.version,
    deleted: true,
  }));
  // One retry: a delete is terminal, so re-tombstone any conflict at the server's version.
  // Rejected records (role-gated on shared projects) are NOT tombstoned locally and NOT
  // retried — the record stays live everywhere, and we surface the denial to the caller.
  let firstRejection: SyncPushRejection | undefined;
  for (let attempt = 0; attempt < 2 && records.length; attempt++) {
    const res = await syncPush(records);
    if (!firstRejection && res.rejected?.length) firstRejection = res.rejected[0];
    for (const a of res.applied) {
      store.apply({
        entityType: a.entityType as EntityType,
        entityId: a.entityId,
        parentId: parentOf.get(a.entityType + "#" + a.entityId) ?? null,
        version: a.version,
        updatedAt: a.updatedAt,
        deleted: true,
        payload: {},
      } as SyncRecord);
    }
    records = res.conflicts.map((c) => ({
      entityType: c.entityType,
      entityId: c.entityId,
      parentId: c.parentId,
      baseVersion: c.version,
      deleted: true,
    }));
  }
  if (firstRejection) throw rejectionError(firstRejection.reason, "delete this");
}

/**
 * Add (or flip) an optimistic in-progress makeover SESSION in the LOCAL store so
 * the room's "Past makeovers" list shows it immediately while the AWS worker
 * generates. Backend-authoritative: this is NEVER pushed — the worker finalizes
 * the SESSION/CYCLE onto the same id and the client replaces this on reload.
 * Mirrors the iOS optimistic stub (pendingPush=false). Returns nothing; caller
 * bumps `rev` via touch().
 */
export function startOptimisticSession(
  store: EntityStore,
  roomId: string,
  sessionId: string,
  goal: string,
): void {
  const existing = store.get<SessionPayload>("SESSION", sessionId);
  const now = new Date().toISOString();
  const payload: SessionPayload = existing
    ? { ...existing.payload, status: "generating" }
    : { goal, createdAt: now, status: "generating" };
  store.apply({
    entityType: "SESSION",
    entityId: sessionId,
    parentId: roomId,
    version: existing?.version ?? 0,
    updatedAt: existing?.updatedAt ?? now,
    deleted: false,
    payload,
  } as unknown as SyncRecord);
}

/** Rename a PROJECT or ROOM — clone the full payload, patch `name`, push. */
export async function renameEntity(
  store: EntityStore,
  type: EntityType,
  id: string,
  newName: string,
): Promise<void> {
  const e = store.get(type, id);
  if (!e) throw new Error("not found");
  const payload = { ...e.payload, name: newName };
  const res = await syncPush([
    {
      entityType: type,
      entityId: id,
      parentId: e.parentId,
      baseVersion: e.version,
      deleted: false,
      payload,
    },
  ]);
  // Role-rejected (e.g. viewer, or membership revoked mid-session): throw BEFORE
  // any local mutation so the store never diverges from the server.
  const rejected = rejectionOf(res, type, id);
  if (rejected) throw rejectionError(rejected.reason, "rename this");
  const conflict = res.conflicts.find((c) => c.entityType === type && c.entityId === id);
  if (conflict) {
    // Server is newer — adopt its copy (pull-wins), then the user can retry.
    store.apply(conflict as SyncRecord);
    throw new Error("This was changed elsewhere; reloaded the latest. Try again.");
  }
  e.payload = payload;
  const applied = res.applied.find((a) => a.entityType === type && a.entityId === id);
  if (applied) store.noteApplied(type, id, applied.version, applied.updatedAt);
}

/**
 * Save a render to the room's Ideabook. The render blob already exists (content-
 * addressed), so the new IDEA simply references the same blob under a fresh local
 * filename — exactly like the iOS "copy + same bytes ⇒ dedup" path. No upload.
 */
export async function saveIdea(
  store: EntityStore,
  roomId: string,
  opts: { renderFileName: string; renderAsset: AssetRef; goal: string },
): Promise<void> {
  const fileName = `${newUuid()}.png`;
  const ideaId = newUuid();
  const asset: AssetRef = {
    localPath: `photos/${fileName}`,
    s3Key: opts.renderAsset.s3Key,
    sha256: opts.renderAsset.sha256,
    bytes: opts.renderAsset.bytes,
    contentType: opts.renderAsset.contentType || "image/png",
    kind: "photo",
  };
  const payload = {
    fileName,
    goal: opts.goal,
    sourceFileName: opts.renderFileName,
    createdAt: new Date().toISOString(),
    assets: [asset],
  };
  const res = await syncPush([
    { entityType: "IDEA", entityId: ideaId, parentId: roomId, baseVersion: 0, deleted: false, payload },
  ]);
  const rejected = rejectionOf(res, "IDEA", ideaId);
  if (rejected) throw rejectionError(rejected.reason, "save ideas");
  const applied = res.applied.find((a) => a.entityType === "IDEA" && a.entityId === ideaId);
  store.apply({
    entityType: "IDEA",
    entityId: ideaId,
    parentId: roomId,
    version: applied?.version ?? 1,
    updatedAt: applied?.updatedAt ?? "",
    deleted: false,
    payload,
  } as SyncRecord);
}

/**
 * Save a job-fallback render (only available as a presigned URL, not yet a synced
 * blob) to the Ideabook: download it, content-address + upload it, then push the IDEA.
 */
export async function saveIdeaFromUrl(
  store: EntityStore,
  roomId: string,
  opts: { url: string; sourceId: string; goal: string },
): Promise<void> {
  const blob = await (await fetch(opts.url)).blob();
  const bytes = await blob.arrayBuffer();
  const sha = await sha256Hex(bytes);
  // In a SHARED project, presign into the project owner's prefix (pass projectId)
  // so the blob stays with the project if this member later leaves.
  const projectId = store.projectIdOf("ROOM", roomId);
  const sharedProjectId = projectId && store.membership(projectId) ? projectId : undefined;
  const [presigned] = await presignPutAssets(
    [{ sha256: sha, bytes: blob.size, contentType: "image/png", kind: "render" }],
    sharedProjectId,
  );
  if (presigned.status === "missing" && presigned.uploadUrl) {
    await putBytes(presigned.uploadUrl, blob, "image/png");
  }
  const fileName = `${newUuid()}.png`;
  const ideaId = newUuid();
  const asset: AssetRef = {
    localPath: `photos/${fileName}`,
    s3Key: presigned.key,
    sha256: sha,
    bytes: blob.size,
    contentType: "image/png",
    kind: "photo",
  };
  const payload = {
    fileName,
    goal: opts.goal,
    sourceFileName: opts.sourceId,
    createdAt: new Date().toISOString(),
    assets: [asset],
  };
  const res = await syncPush([
    { entityType: "IDEA", entityId: ideaId, parentId: roomId, baseVersion: 0, deleted: false, payload },
  ]);
  const rejected = rejectionOf(res, "IDEA", ideaId);
  if (rejected) throw rejectionError(rejected.reason, "save ideas");
  const applied = res.applied.find((a) => a.entityType === "IDEA" && a.entityId === ideaId);
  store.apply({
    entityType: "IDEA",
    entityId: ideaId,
    parentId: roomId,
    version: applied?.version ?? 1,
    updatedAt: applied?.updatedAt ?? "",
    deleted: false,
    payload,
  } as SyncRecord);
}
